import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  UserPlus, MoreVertical, Copy, RefreshCw, Trash2,
  Loader2, AlertTriangle,
  ChevronRight,
} from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { useBranch } from "@/context/BranchContext";
import { sendInvitationEmail } from "@/lib/apiClient";
import { copyTextToClipboard } from "@/lib/clipboard";

// =====================================================================
// Settings → Team
//   Top   : active members list (role dropdown + remove)
//   Bottom: pending invitations (revoke + resend + copy link)
//   CTA   : + Invite Member (dialog)
// =====================================================================

const ROLES = [
  { value: "clinic_admin", label: "Clinic Admin" },
  { value: "doctor",       label: "Doctor" },
  { value: "receptionist", label: "Receptionist" },
  { value: "assistant",    label: "Assistant" },
];
const ROLE_LABEL = Object.fromEntries(ROLES.map(r => [r.value, r.label]));
const PROCEDURE_CATEGORIES = [
  "Cosmetic",
  "Diagnostic",
  "Emergency",
  "Endodontic",
  "Follow-up",
  "Implant",
  "Oral Surgery",
  "Orthodontic",
  "Pediatric",
  "Periodontal",
  "Preventive",
  "Prosthodontic",
  "Restorative",
];

export default function SettingsTeam() {
  const { user, activeClinic } = useAuth();
  const { branches = [] } = useBranch();
  const [searchParams] = useSearchParams();
  const focusedDoctorId = searchParams.get("doctor");
  const activeClinicId = activeClinic?.clinic_id || activeClinic?.id || null;
  const youAreAdmin = (activeClinic?.role === "clinic_admin");

  const [members, setMembers]         = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [capacity, setCapacity]       = useState(null);
  const [loading, setLoading]         = useState(true);
  const [inviteOpen, setInviteOpen]   = useState(false);
  const [rosterInvite, setRosterInvite] = useState(null);
  // Per-row pending PRC values. Reset whenever members reloads so the
  // input snaps back to the server-canonical value after a save.
  const [prcEdits, setPrcEdits]       = useState({});
  const [displayNameEdits, setDisplayNameEdits] = useState({});
  const [commissionRates, setCommissionRates] = useState({});
  const [commissionEdits, setCommissionEdits] = useState({});
  const [savingCommissions, setSavingCommissions] = useState({});
  const [savedCommissions, setSavedCommissions] = useState({});
  const [commissionEffectiveDates, setCommissionEffectiveDates] = useState({});
  const [categoryOverrideRates, setCategoryOverrideRates] = useState({});
  const [categoryOverrideEffectiveDates, setCategoryOverrideEffectiveDates] = useState({});
  const [categoryOverrideEdits, setCategoryOverrideEdits] = useState({});
  const [expandedCategoryOverrides, setExpandedCategoryOverrides] = useState({});
  const [commissionPayoutBasis, setCommissionPayoutBasis] = useState("on_collection");
  const [canManageCommissionPayoutBasis, setCanManageCommissionPayoutBasis] = useState(false);
  const [savingCommissionBasis, setSavingCommissionBasis] = useState(false);
  const [staffBranchMode, setStaffBranchMode] = useState("shared");
  const [canManageStaffBranchMode, setCanManageStaffBranchMode] = useState(false);
  const [savingStaffBranchMode, setSavingStaffBranchMode] = useState(false);
  const [savingStaffBranches, setSavingStaffBranches] = useState({});
  const [savingDoctorAccess, setSavingDoctorAccess] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    const [m, i, c, staffBranchSettings] = await Promise.all([
      supabase.rpc("list_clinic_members"),
      supabase.rpc("list_pending_invitations"),
      supabase.rpc("get_team_plan_capacity"),
      supabase.rpc("get_staff_branch_assignment_settings"),
    ]);
    const memberRows = m.data ?? [];
    let currentCommissionRates = {};
    let currentCommissionEffectiveDates = {};
    let currentCategoryOverrideRates = {};
    let currentCategoryOverrideEffectiveDates = {};
    if (youAreAdmin && activeClinicId) {
      const [baseRates, categoryOverrides, commissionSettings] = await Promise.all([
        supabase
          .from("doctor_commission_settings")
          .select("doctor_id, commission_rate, effective_from, created_at")
          .eq("clinic_id", activeClinicId)
          .lte("effective_from", todayIsoDate())
          .order("effective_from", { ascending: false })
          .order("created_at", { ascending: false }),
        supabase
          .from("doctor_commission_category_overrides")
          .select("doctor_id, category, commission_rate, effective_from, created_at")
          .eq("clinic_id", activeClinicId)
          .lte("effective_from", todayIsoDate())
          .order("effective_from", { ascending: false })
          .order("created_at", { ascending: false }),
        supabase.rpc("get_commission_settings"),
      ]);
      if (baseRates.error) {
        toast.error(baseRates.error.message || "Could not load doctor commission rates.");
      } else {
        for (const row of baseRates.data ?? []) {
          if (row?.doctor_id && currentCommissionRates[row.doctor_id] === undefined) {
            currentCommissionRates[row.doctor_id] = normalizeCommissionRate(row.commission_rate);
            currentCommissionEffectiveDates[row.doctor_id] = dateOnly(row.effective_from);
          }
        }
      }
      if (categoryOverrides.error) {
        toast.error(categoryOverrides.error.message || "Could not load category commission overrides.");
      } else {
        for (const row of categoryOverrides.data ?? []) {
          const key = categoryOverrideKey(row?.doctor_id, row?.category);
          if (key && currentCategoryOverrideRates[key] === undefined) {
            currentCategoryOverrideRates[key] = normalizeCommissionRate(row.commission_rate);
            currentCategoryOverrideEffectiveDates[key] = dateOnly(row.effective_from);
          }
        }
      }
      if (commissionSettings.error) {
        toast.error(commissionSettings.error.message || "Could not load commission payout setting.");
        setCanManageCommissionPayoutBasis(false);
      } else {
        setCommissionPayoutBasis(commissionSettings.data?.commission_payout_basis || "on_collection");
        setCanManageCommissionPayoutBasis(Boolean(commissionSettings.data?.can_manage_payout_basis));
      }
    } else {
      setCanManageCommissionPayoutBasis(false);
    }
    setMembers(memberRows);
    setInvitations(i.data ?? []);
    setCapacity(c.error ? null : c.data);
    if (staffBranchSettings.error) {
      toast.error(staffBranchSettings.error.message || "Could not load branch assignment settings.");
      setStaffBranchMode("shared");
      setCanManageStaffBranchMode(false);
    } else {
      setStaffBranchMode(staffBranchSettings.data?.mode || "shared");
      setCanManageStaffBranchMode(Boolean(staffBranchSettings.data?.can_manage));
    }
    setPrcEdits({});
    setDisplayNameEdits({});
    setCommissionRates(currentCommissionRates);
    setCommissionEffectiveDates(currentCommissionEffectiveDates);
    setCommissionEdits({});
    setCategoryOverrideRates(currentCategoryOverrideRates);
    setCategoryOverrideEffectiveDates(currentCategoryOverrideEffectiveDates);
    setCategoryOverrideEdits({});
    setLoading(false);
  }, [activeClinicId, youAreAdmin]);
  useEffect(() => { load(); }, [load]);

  const loadCommissionForDoctor = useCallback(async (doctorId) => {
    if (!youAreAdmin || !activeClinicId || !doctorId) return;

    const [baseRates, categoryOverrides] = await Promise.all([
      supabase
        .from("doctor_commission_settings")
        .select("doctor_id, commission_rate, effective_from, created_at")
        .eq("clinic_id", activeClinicId)
        .eq("doctor_id", doctorId)
        .lte("effective_from", todayIsoDate())
        .order("effective_from", { ascending: false })
        .order("created_at", { ascending: false }),
      supabase
        .from("doctor_commission_category_overrides")
        .select("doctor_id, category, commission_rate, effective_from, created_at")
        .eq("clinic_id", activeClinicId)
        .eq("doctor_id", doctorId)
        .lte("effective_from", todayIsoDate())
        .order("effective_from", { ascending: false })
        .order("created_at", { ascending: false }),
    ]);

    if (baseRates.error) {
      toast.error(baseRates.error.message || "Could not reload doctor commission rate.");
    } else {
      const latestBaseRow = baseRates.data?.[0];
      const latestBase = latestBaseRow?.commission_rate;
      setCommissionRates((prev) => ({ ...prev, [doctorId]: normalizeCommissionRate(latestBase ?? 0) }));
      setCommissionEffectiveDates((prev) => {
        const next = { ...prev };
        const effectiveDate = dateOnly(latestBaseRow?.effective_from);
        if (effectiveDate) {
          next[doctorId] = effectiveDate;
        } else {
          delete next[doctorId];
        }
        return next;
      });
      setCommissionEdits((prev) => {
        const next = { ...prev };
        delete next[doctorId];
        return next;
      });
    }

    if (categoryOverrides.error) {
      toast.error(categoryOverrides.error.message || "Could not reload category commission overrides.");
      return;
    }

    const nextDoctorOverrides = {};
    const nextDoctorOverrideEffectiveDates = {};
    for (const row of categoryOverrides.data ?? []) {
      const key = categoryOverrideKey(row?.doctor_id, row?.category);
      if (key && nextDoctorOverrides[key] === undefined) {
        nextDoctorOverrides[key] = normalizeCommissionRate(row.commission_rate);
        nextDoctorOverrideEffectiveDates[key] = dateOnly(row.effective_from);
      }
    }
    setCategoryOverrideRates((prev) => {
      const next = { ...prev };
      for (const category of PROCEDURE_CATEGORIES) {
        delete next[categoryOverrideKey(doctorId, category)];
      }
      return { ...next, ...nextDoctorOverrides };
    });
    setCategoryOverrideEffectiveDates((prev) => {
      const next = { ...prev };
      for (const category of PROCEDURE_CATEGORIES) {
        delete next[categoryOverrideKey(doctorId, category)];
      }
      return { ...next, ...nextDoctorOverrideEffectiveDates };
    });
    setCategoryOverrideEdits((prev) => {
      const next = { ...prev };
      for (const category of PROCEDURE_CATEGORIES) {
        delete next[categoryOverrideKey(doctorId, category)];
      }
      return next;
    });
  }, [activeClinicId, youAreAdmin]);

  async function handleChangeRole(member, newRole) {
    if (member.role === newRole) return;
    const { data, error } = await supabase.rpc("update_member_role", {
      p_user_id: member.user_id, p_new_role: newRole,
    });
    if (error) { toast.error(error.message); return; }
    if (data?.ok) toast.success(`Role updated to ${ROLE_LABEL[newRole]}.`);
    load();
  }

  async function handleChangePrc(member, raw) {
    const next = (raw ?? "").trim();
    const current = (member.prc_license ?? "").trim();
    if (next === current) return;
    const { error } = await supabase.rpc("update_member_prc_license", {
      p_user_id: member.user_id,
      p_prc:     next,
    });
    if (error) { toast.error(error.message); return; }
    toast.success(next ? "PRC license saved." : "PRC license cleared.");
    load();
  }

  async function handleChangeDisplayName(member, raw) {
    const next = (raw ?? "").trim();
    const current = (member.doctor_display_name ?? "").trim();
    if (next === current) return;
    if (!next) {
      toast.error("Doctor display name is required.");
      setDisplayNameEdits((prev) => ({ ...prev, [member.user_id]: current }));
      return;
    }
    const { error } = await supabase.rpc("update_member_doctor_display_name", {
      p_user_id: member.user_id,
      p_display_name: next,
    });
    if (error) { toast.error(error.message); return; }
    toast.success("Doctor display name saved.");
    load();
  }

  async function handleSaveCommission(member) {
    if (!youAreAdmin) return;
    const doctorId = member?.doctor_id;
    if (!doctorId) {
      toast.error("This doctor row is missing a doctor record.");
      return;
    }
    if (!activeClinicId) {
      toast.error("No active clinic selected.");
      return;
    }
    const raw = commissionEdits[doctorId] !== undefined
      ? commissionEdits[doctorId]
      : formatCommissionPercent(commissionRates[doctorId] ?? 0);
    const parsed = parseCommissionPercent(raw);
    if (parsed === null) {
      toast.error("Commission must be a number from 0 to 100 with up to 2 decimals.");
      return;
    }

    const categoryOverrides = [];
    const today = todayIsoDate();
    const nextBaseRate = parsed / 100;
    const currentBaseRate = normalizeCommissionRate(commissionRates[doctorId] ?? 0);
    const baseChanged = !commissionRatesEqual(nextBaseRate, currentBaseRate);
    if (baseChanged) {
      if (commissionEffectiveDates[doctorId] === today) {
        toast.error("A base commission rate already exists for today. Change it tomorrow to keep the append-only history.");
        return;
      }
    }

    for (const category of PROCEDURE_CATEGORIES) {
      const key = categoryOverrideKey(doctorId, category);
      const categoryRaw = categoryOverrideEdits[key] !== undefined
        ? categoryOverrideEdits[key]
        : (categoryOverrideRates[key] === undefined ? "" : formatCommissionPercent(categoryOverrideRates[key]));
      if (String(categoryRaw ?? "").trim() === "") continue;
      const categoryParsed = parseCommissionPercent(categoryRaw);
      if (categoryParsed === null) {
        toast.error(`${category} commission must be a number from 0 to 100 with up to 2 decimals.`);
        return;
      }
      const nextCategoryRate = categoryParsed / 100;
      const currentCategoryRate = categoryOverrideRates[key];
      if (currentCategoryRate !== undefined && commissionRatesEqual(nextCategoryRate, currentCategoryRate)) {
        continue;
      }
      if (categoryOverrideEffectiveDates[key] === today) {
        toast.error(`${category} commission already exists for today. Change it tomorrow to keep the append-only history.`);
        return;
      }
      categoryOverrides.push({
        category,
        commission_rate: nextCategoryRate,
      });
    }

    if (!baseChanged && categoryOverrides.length === 0) {
      setSavedCommissions((prev) => ({ ...prev, [doctorId]: true }));
      window.setTimeout(() => {
        setSavedCommissions((prev) => ({ ...prev, [doctorId]: false }));
      }, 2000);
      return;
    }

    setSavingCommissions((prev) => ({ ...prev, [doctorId]: true }));
    const { error } = await supabase.rpc("save_doctor_commission_settings", {
      p_doctor_id: doctorId,
      p_base_commission_rate: baseChanged ? nextBaseRate : null,
      p_category_overrides: categoryOverrides,
      p_effective_from: today,
      p_notes: null,
    });
    setSavingCommissions((prev) => ({ ...prev, [doctorId]: false }));
    if (error) {
      toast.error(error.message || "Could not save commission settings.");
      return;
    }
    await loadCommissionForDoctor(doctorId);
    setSavedCommissions((prev) => ({ ...prev, [doctorId]: true }));
    window.setTimeout(() => {
      setSavedCommissions((prev) => ({ ...prev, [doctorId]: false }));
    }, 2000);
  }

  async function handleCommissionPayoutBasisChange(nextBasis) {
    if (!youAreAdmin || !canManageCommissionPayoutBasis || nextBasis === commissionPayoutBasis) return;
    setSavingCommissionBasis(true);
    const { data, error } = await supabase.rpc("set_commission_payout_basis", {
      p_basis: nextBasis,
    });
    setSavingCommissionBasis(false);
    if (error) {
      toast.error(error.message || "Could not save commission payout timing.");
      return;
    }
    setCommissionPayoutBasis(data?.commission_payout_basis || nextBasis);
    toast.success("Commission payout timing saved.");
  }

  async function handleStaffBranchModeChange(nextMode) {
    if (!youAreAdmin || !canManageStaffBranchMode || savingStaffBranchMode || staffBranchMode === nextMode) return;
    setSavingStaffBranchMode(true);
    const { data, error } = await supabase.rpc("set_staff_branch_assignment_mode", {
      p_mode: nextMode,
    });
    setSavingStaffBranchMode(false);
    if (error) {
      toast.error(error.message || "Could not save staff branch assignment.");
      return;
    }
    setStaffBranchMode(data?.mode || nextMode);
    toast.success(nextMode === "branch_assigned"
      ? "Staff branch assignment enabled."
      : "Staff can work in every branch.");
  }

  async function handleStaffBranchChange(member, branchId) {
    if (!youAreAdmin || !canManageStaffBranchMode || !branchId) return;
    const rowKey = memberRowKey(member);
    setSavingStaffBranches((prev) => ({ ...prev, [rowKey]: true }));
    const { error } = await supabase.rpc("set_staff_member_branch", {
      p_user_id: member.user_id ?? null,
      p_staff_id: member.staff_id ?? null,
      p_branch_id: branchId,
    });
    setSavingStaffBranches((prev) => ({ ...prev, [rowKey]: false }));
    if (error) {
      toast.error(error.message || "Could not save staff branch.");
      return;
    }
    const branchName = branchLabel(branches, branchId);
    setMembers((prev) => prev.map((row) => (
      memberRowKey(row) === rowKey
        ? { ...row, staff_branch_id: branchId, staff_branch_name: branchName }
        : row
    )));
    toast.success("Staff branch saved.");
  }

  async function saveDoctorBranchAccess(member, homeBranchId, grantIds) {
    if (!youAreAdmin || !canManageStaffBranchMode || !member?.doctor_id || !homeBranchId) return;
    const rowKey = memberRowKey(member);
    const cleanGrantIds = uniqueIds(grantIds).filter((id) => id && id !== homeBranchId);
    setSavingDoctorAccess((prev) => ({ ...prev, [rowKey]: true }));
    const { error } = await supabase.rpc("set_doctor_branch_access", {
      p_doctor_id: member.doctor_id,
      p_home_branch_id: homeBranchId,
      p_granted_branch_ids: cleanGrantIds,
    });
    setSavingDoctorAccess((prev) => ({ ...prev, [rowKey]: false }));
    if (error) {
      toast.error(error.message || "Could not save doctor branch access.");
      return;
    }
    const homeName = branchLabel(branches, homeBranchId);
    setMembers((prev) => prev.map((row) => (
      memberRowKey(row) === rowKey
        ? {
          ...row,
          doctor_home_branch_id: homeBranchId,
          doctor_home_branch_name: homeName,
          doctor_branch_grant_ids: cleanGrantIds,
        }
        : row
    )));
    toast.success("Doctor branch access saved.");
  }

  function handleDoctorHomeBranchChange(member, homeBranchId) {
    const grantIds = uniqueIds(member?.doctor_branch_grant_ids || []);
    saveDoctorBranchAccess(member, homeBranchId, grantIds);
  }

  function handleDoctorGrantToggle(member, branchId, checked) {
    const homeBranchId = member?.doctor_home_branch_id || branches[0]?.id || "";
    const current = uniqueIds(member?.doctor_branch_grant_ids || []);
    const next = checked
      ? uniqueIds([...current, branchId])
      : current.filter((id) => id !== branchId);
    saveDoctorBranchAccess(member, homeBranchId, next);
  }

  async function handleRemoveMember(member) {
    if (member.roster_only) {
      if (!window.confirm(`Remove ${member.full_name || "this person"} from the roster?`)) return;
      const { error } = await supabase.rpc("archive_roster_member", {
        p_doctor_id: member.doctor_id ?? null,
        p_staff_id: member.staff_id ?? null,
      });
      if (error) { toast.error(error.message); return; }
      toast.success("Roster entry removed.");
      load();
      return;
    }

    if (!window.confirm(`Remove ${member.full_name || member.email} from this clinic?`)) return;
    const { error } = await supabase.rpc("remove_member_from_clinic", { p_user_id: member.user_id });
    if (error) { toast.error(error.message); return; }
    toast.success("Member removed.");
    load();
  }

  async function handleRevoke(inv) {
    const reason = window.prompt("Reason for revoking (optional):") || "";
    const { error } = await supabase.rpc("revoke_invitation", {
      p_invitation_id: inv.id, p_reason: reason,
    });
    if (error) { toast.error(error.message); return; }
    toast.success("Invitation revoked.");
    load();
  }

  async function handleResend(inv) {
    const { data, error } = await supabase.rpc("resend_invitation", { p_invitation_id: inv.id });
    if (error) { toast.error(error.message); return; }
    const mail = await sendInvitationEmail({
      to_email:    inv.email,
      clinic_name: data.clinic_name,
      clinic_slug: data.clinic_slug,
      role:        inv.role,
      code:        inv.code,
      invited_by:  data.invited_by || user?.user_metadata?.full_name || user?.email || "The clinic admin",
    });
    if (mail.ok) toast.success(`Invitation resent to ${inv.email}.`);
    else         toast.warning(`Re-sent in DB, but email failed (${mail.code}). Use Copy Link.`);
    load();
  }

  async function handleCopyLink(inv) {
    if (!isInvitationActionable(inv)) {
      toast.error("This invitation has expired. Send a new invitation before copying a link.");
      return;
    }
    await copyInvitationUrl(invitationUrl(inv.code));
  }

  return (
    <div className="space-y-6" data-testid="settings-team-page" data-tutorial-id="settings-team-page">
      <header className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Team</h1>
          <p className="text-sm text-slate-500 mt-1">
            Invite staff, change roles, and manage access to <span className="font-medium">{activeClinic?.clinic_name}</span>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="invite-member-button"
            data-tutorial-id="settings-team-invite-member"
            onClick={() => setInviteOpen(true)}
            disabled={!youAreAdmin}
            title="Send an email invite link (good for remote staff)"
            className="inline-flex items-center gap-2 rounded-lg bg-teal-600 hover:bg-teal-700 disabled:bg-slate-300 text-white text-sm font-semibold px-4 py-2"
          >
            <UserPlus className="w-4 h-4" strokeWidth={2} /> Invite Member
          </button>
        </div>
      </header>

      {capacity ? <PlanCapacity capacity={capacity} /> : null}

      {youAreAdmin ? (
        <section
          className="rounded-xl border border-slate-200 bg-white px-5 py-4 shadow-sm"
          data-testid="staff-branch-mode-section"
        >
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="max-w-2xl">
              <h2 className="text-sm font-semibold text-slate-900">Staff branch access</h2>
              <p className="mt-1 text-xs leading-relaxed text-slate-500">
                Receptionists and assistants can either work in every branch or be assigned to one branch.
              </p>
            </div>
            <div className="grid gap-2 sm:grid-cols-2" role="group" aria-label="Staff branch access">
              <button
                type="button"
                disabled={savingStaffBranchMode || !canManageStaffBranchMode}
                onClick={() => handleStaffBranchModeChange("shared")}
                aria-pressed={staffBranchMode !== "branch_assigned"}
                data-testid="staff-branch-mode-shared"
                className={`rounded-lg border px-3 py-2 text-left text-xs transition-colors ${
                  staffBranchMode !== "branch_assigned"
                    ? "border-teal-500 bg-teal-50 text-teal-900"
                    : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                } disabled:opacity-60`}
              >
                <span className="block font-semibold">Shared</span>
                <span className="mt-0.5 block leading-snug text-slate-500">Staff can work in any branch.</span>
              </button>
              <button
                type="button"
                disabled={savingStaffBranchMode || !canManageStaffBranchMode}
                onClick={() => handleStaffBranchModeChange("branch_assigned")}
                aria-pressed={staffBranchMode === "branch_assigned"}
                data-testid="staff-branch-mode-branch-assigned"
                className={`rounded-lg border px-3 py-2 text-left text-xs transition-colors ${
                  staffBranchMode === "branch_assigned"
                    ? "border-teal-500 bg-teal-50 text-teal-900"
                    : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                } disabled:opacity-60`}
              >
                <span className="block font-semibold">Branch-assigned</span>
                <span className="mt-0.5 block leading-snug text-slate-500">Each staff member works from one branch.</span>
              </button>
            </div>
          </div>
        </section>
      ) : null}

      {youAreAdmin && canManageCommissionPayoutBasis ? (
        <section
          className="rounded-xl border border-slate-200 bg-white px-5 py-4 shadow-sm"
          data-testid="commission-payout-basis-section"
          data-tutorial-id="settings-team-commission-payout-basis"
        >
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="max-w-2xl">
              <h2 className="text-sm font-semibold text-slate-900">Commission payout timing</h2>
              <p className="mt-1 text-xs leading-relaxed text-slate-500">
                Production reports are unaffected. This only controls when commission becomes payable.
              </p>
            </div>
            <div className="grid gap-2 sm:grid-cols-2" role="group" aria-label="Commission payout timing">
              <button
                type="button"
                disabled={savingCommissionBasis}
                onClick={() => handleCommissionPayoutBasisChange("on_completion")}
                data-testid="commission-payout-on-completion"
                className={`rounded-lg border px-3 py-2 text-left text-xs transition-colors ${
                  commissionPayoutBasis === "on_completion"
                    ? "border-teal-500 bg-teal-50 text-teal-900"
                    : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                } disabled:opacity-60`}
              >
                <span className="block font-semibold">On completion</span>
                <span className="mt-0.5 block leading-snug text-slate-500">Payable as soon as the procedure is completed.</span>
              </button>
              <button
                type="button"
                disabled={savingCommissionBasis}
                onClick={() => handleCommissionPayoutBasisChange("on_collection")}
                data-testid="commission-payout-on-collection"
                className={`rounded-lg border px-3 py-2 text-left text-xs transition-colors ${
                  commissionPayoutBasis === "on_collection"
                    ? "border-teal-500 bg-teal-50 text-teal-900"
                    : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                } disabled:opacity-60`}
              >
                <span className="block font-semibold">On collection</span>
                <span className="mt-0.5 block leading-snug text-slate-500">Payable only as patient payments are collected.</span>
              </button>
            </div>
          </div>
        </section>
      ) : null}

      {/* Members */}
      <section className="bg-white rounded-xl shadow-sm" data-testid="members-section" data-tutorial-id="settings-team-members">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-900">Active members</h2>
          <span className="text-xs text-slate-500">{members.length} total</span>
        </div>
        {loading ? (
          <div className="px-6 py-10 text-center text-sm text-slate-400"><Loader2 className="inline w-4 h-4 animate-spin"/> Loading…</div>
        ) : members.length === 0 ? (
          <div className="px-6 py-10 text-center text-sm text-slate-400">No members yet.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {members.map((m) => {
              const rowKey = m.user_id || m.doctor_id || m.staff_id;
              const isRosterOnly = !!m.roster_only;
              const prcValue = prcEdits[rowKey] !== undefined
                ? prcEdits[rowKey]
                : (m.prc_license || "");
              const displayNameValue = displayNameEdits[rowKey] !== undefined
                ? displayNameEdits[rowKey]
                : (m.doctor_display_name || "");
              const isDoctor = m.role === "doctor" || Boolean(m.doctor_id);
              const rowBranches = Array.isArray(branches) ? branches : [];
              const commissionKey = m.doctor_id;
              const commissionValue = commissionEdits[commissionKey] !== undefined
                ? commissionEdits[commissionKey]
                : formatCommissionPercent(commissionRates[commissionKey] ?? 0);
              const activeOverrideCount = countActiveCategoryOverrides(
                commissionKey,
                categoryOverrideRates,
                categoryOverrideEdits
              );
              const commissionSaving = !!savingCommissions[commissionKey];
              const commissionSaved = !!savedCommissions[commissionKey];
              const isFocusedDoctor = focusedDoctorId
                && (m.doctor_id === focusedDoctorId || m.user_id === focusedDoctorId);
              return (
                <li
                  key={rowKey}
                  data-testid={`member-row-${rowKey}`}
                  data-tutorial-id={isDoctor ? "settings-team-doctor-row" : undefined}
                  className={`${isFocusedDoctor ? "rounded-lg bg-amber-50 ring-1 ring-amber-200" : ""}`}
                >
                  <div className="flex items-center gap-4 px-6 py-3">
                    <div className="w-9 h-9 rounded-full bg-teal-50 text-teal-700 font-semibold flex items-center justify-center">
                      {(m.full_name || m.email || "R").slice(0,1).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-slate-900 truncate">
                        {m.full_name || m.email}
                        {m.is_you ? <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-400">You</span> : null}
                        {isRosterOnly ? <span className="ml-2 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700">Roster only — no login yet</span> : null}
                      </div>
                      <div className="text-xs text-slate-500 truncate">{m.email || "No login email yet"}</div>
                    </div>
                    {isDoctor ? (
                      <div className="flex items-center gap-2">
                        <input
                          data-testid={`member-display-name-${rowKey}`}
                          type="text"
                          value={displayNameValue}
                          onChange={(e) => setDisplayNameEdits((prev) => ({ ...prev, [rowKey]: e.target.value }))}
                          onBlur={() => !isRosterOnly && handleChangeDisplayName(m, displayNameValue)}
                          disabled={!youAreAdmin || isRosterOnly}
                          placeholder="Display name"
                          maxLength={80}
                          className="w-40 rounded-md border border-slate-200 px-2 py-1 text-xs disabled:opacity-50"
                          title="Doctor name shown on bookings, Tx Plan PDFs, and patient-facing documents"
                        />
                        <input
                          data-testid={`member-prc-${rowKey}`}
                          type="text"
                          value={prcValue}
                          onChange={(e) => setPrcEdits((prev) => ({ ...prev, [rowKey]: e.target.value }))}
                          onBlur={() => !isRosterOnly && handleChangePrc(m, prcValue)}
                          disabled={!youAreAdmin || isRosterOnly}
                          placeholder="PRC #"
                          maxLength={60}
                          className="w-32 rounded-md border border-slate-200 px-2 py-1 text-xs disabled:opacity-50"
                          title="PRC license shown on Tx Plan PDFs prepared by this doctor"
                        />
                      </div>
                    ) : null}
                    {!isDoctor && staffBranchMode === "branch_assigned" ? (
                      <select
                        data-testid={`staff-branch-select-${rowKey}`}
                        value={m.staff_branch_id || ""}
                        onChange={(e) => handleStaffBranchChange(m, e.target.value)}
                        disabled={!youAreAdmin || !canManageStaffBranchMode || savingStaffBranches[rowKey] || rowBranches.length === 0}
                        className="rounded-md border border-slate-200 px-2 py-1 text-xs bg-white"
                        title="Branch where this staff member works when branch assignment is enabled"
                      >
                        <option value="" disabled>Pick branch</option>
                        {rowBranches.map((branch) => (
                          <option key={branch.id} value={branch.id}>{branch.name}</option>
                        ))}
                      </select>
                    ) : null}
                    {youAreAdmin && isDoctor && rowBranches.length > 0 ? (
                      <div className="flex items-center gap-2">
                        <select
                          data-testid={`doctor-home-branch-${rowKey}`}
                          value={m.doctor_home_branch_id || rowBranches[0]?.id || ""}
                          onChange={(e) => handleDoctorHomeBranchChange(m, e.target.value)}
                          disabled={!canManageStaffBranchMode || !m.doctor_id || savingDoctorAccess[rowKey]}
                          className="rounded-md border border-slate-200 px-2 py-1 text-xs bg-white"
                          title="Doctor home branch"
                        >
                          {rowBranches.map((branch) => (
                            <option key={branch.id} value={branch.id}>{branch.name}</option>
                          ))}
                        </select>
                        <div className="flex items-center gap-1.5">
                          {rowBranches
                            .filter((branch) => branch.id !== (m.doctor_home_branch_id || rowBranches[0]?.id))
                            .map((branch) => (
                              <label
                                key={branch.id}
                                className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-600"
                                title={`Allow scheduling in ${branch.name}`}
                              >
                                <input
                                  type="checkbox"
                                  data-testid={`doctor-branch-grant-${rowKey}-${branch.id}`}
                                  checked={uniqueIds(m.doctor_branch_grant_ids || []).includes(branch.id)}
                                  onChange={(e) => handleDoctorGrantToggle(m, branch.id, e.target.checked)}
                                  disabled={!canManageStaffBranchMode || !m.doctor_id || savingDoctorAccess[rowKey]}
                                  className="h-3.5 w-3.5 rounded border-slate-300 text-teal-600 focus:ring-teal-300"
                                />
                                {branch.name}
                              </label>
                            ))}
                        </div>
                      </div>
                    ) : null}
                    {isRosterOnly ? (
                      <button
                        type="button"
                        data-testid={`roster-send-invite-${rowKey}`}
                        data-tutorial-id="settings-team-roster-invite"
                        onClick={() => setRosterInvite(m)}
                        disabled={!youAreAdmin}
                        className="rounded-md border border-teal-200 px-3 py-1.5 text-xs font-semibold text-teal-700 hover:bg-teal-50 disabled:opacity-50"
                      >
                        Send invite
                      </button>
                    ) : null}
                    <select
                      data-testid={`member-role-${rowKey}`}
                      data-tutorial-id={isDoctor ? "settings-team-doctor-role" : undefined}
                      value={m.role}
                      onChange={(e) => handleChangeRole(m, e.target.value)}
                      disabled={!youAreAdmin || m.is_you || isRosterOnly}
                      className="rounded-md border border-slate-200 px-2 py-1 text-xs bg-white"
                    >
                      {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                    <button
                      data-testid={`member-remove-${rowKey}`}
                      onClick={() => handleRemoveMember(m)}
                      disabled={!youAreAdmin || m.is_you}
                      className="p-1.5 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 disabled:opacity-30 disabled:hover:bg-transparent"
                      title={isRosterOnly ? "Remove from roster" : "Remove from clinic"}
                    ><Trash2 className="w-4 h-4" strokeWidth={2}/></button>
                  </div>
                  {youAreAdmin && isDoctor ? (
                    <div
                      data-testid={`member-commission-panel-${rowKey}`}
                      data-tutorial-id="settings-team-commission-panel"
                      className="border-t border-slate-100 bg-slate-50 px-6 py-1.5"
                    >
                      <div
                        data-testid={`member-commission-panel-inner-${rowKey}`}
                        className="ml-[52px] max-w-4xl space-y-1"
                      >
                        <div
                          data-testid={`member-base-commission-block-${rowKey}`}
                          className="rounded-md border border-slate-200 bg-white px-3 py-1.5"
                        >
                          <label className="flex items-center justify-between gap-3">
                            <span>
                              <span className="block text-[11px] font-semibold text-slate-800">Base commission rate</span>
                              <span
                                data-testid={`member-base-commission-subtitle-${rowKey}`}
                                className="mt-0.5 block text-[11px] leading-tight text-slate-500"
                              >
                                Applied to all procedures unless overridden below
                              </span>
                            </span>
                            <span className="flex items-center gap-1.5">
                              <button
                                type="button"
                                data-testid={`member-category-overrides-${rowKey}`}
                                data-tutorial-id="settings-team-category-overrides-toggle"
                                onClick={() => setExpandedCategoryOverrides((prev) => ({ ...prev, [commissionKey]: !prev[commissionKey] }))}
                                className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-600 hover:border-slate-400 hover:text-slate-800"
                                title="Set optional category-specific commission rates"
                              >
                                <ChevronRight className={`h-3 w-3 transition-transform ${expandedCategoryOverrides[commissionKey] ? "rotate-90" : ""}`} />
                                <span
                                  data-testid={`member-category-overrides-status-${rowKey}`}
                                  className={activeOverrideCount > 0 ? "text-teal-700" : "text-slate-500"}
                                >
                                  {activeOverrideCount > 0 ? `${activeOverrideCount} active` : "none set"}
                                </span>
                                {expandedCategoryOverrides[commissionKey] ? "collapse" : "expand"}
                              </button>
                              <input
                                data-testid={`member-base-commission-${rowKey}`}
                                data-tutorial-id="settings-team-base-commission"
                                type="number"
                                min="0"
                                max="100"
                                step="0.01"
                                inputMode="decimal"
                                value={commissionValue}
                                onChange={(e) => setCommissionEdits((prev) => ({ ...prev, [commissionKey]: e.target.value }))}
                                className="w-16 rounded-md border border-slate-200 bg-white px-2 py-1 text-right text-xs font-semibold text-slate-900 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
                              />
                              <span className="text-xs font-semibold text-slate-500">%</span>
                            </span>
                          </label>
                        </div>

                        {expandedCategoryOverrides[commissionKey] ? (
                          <>
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-semibold text-slate-700">Category overrides</span>
                              <span className={`rounded-full px-1.5 py-0.5 text-[11px] font-semibold ${activeOverrideCount > 0 ? "bg-teal-50 text-teal-700" : "bg-slate-100 text-slate-500"}`}>
                                {activeOverrideCount > 0 ? `${activeOverrideCount} active` : "none set"}
                              </span>
                            </div>

                            <div
                              data-testid={`member-category-overrides-grid-${rowKey}`}
                              data-tutorial-id="settings-team-category-overrides-grid"
                              className="grid grid-cols-4 gap-1.5"
                            >
                              {PROCEDURE_CATEGORIES.map((category) => {
                                const overrideKey = categoryOverrideKey(commissionKey, category);
                                const overrideValue = categoryOverrideEdits[overrideKey] !== undefined
                                  ? categoryOverrideEdits[overrideKey]
                                  : (categoryOverrideRates[overrideKey] === undefined ? "" : formatCommissionPercent(categoryOverrideRates[overrideKey]));
                                const hasActiveOverride = String(overrideValue ?? "").trim() !== "";
                                return (
                                  <label
                                    key={category}
                                    data-testid={`member-category-override-cell-${rowKey}-${category}`}
                                    className={`min-w-0 rounded-md border px-2 py-1.5 text-xs ${hasActiveOverride ? "border-teal-200 bg-teal-50/70" : "border-slate-200 bg-white"}`}
                                  >
                                    <span className={`block truncate font-semibold ${hasActiveOverride ? "text-teal-800" : "text-slate-600"}`}>{category}</span>
                                    <span className="mt-1 flex items-center gap-1.5">
                                      <input
                                        data-testid={`member-category-override-input-${rowKey}-${category}`}
                                        type="number"
                                        min="0"
                                        max="100"
                                        step="0.01"
                                        inputMode="decimal"
                                        value={overrideValue}
                                        placeholder="use base"
                                        onChange={(e) => setCategoryOverrideEdits((prev) => ({ ...prev, [overrideKey]: e.target.value }))}
                                        className="min-w-0 flex-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-right text-xs font-semibold text-slate-900 outline-none placeholder:text-left placeholder:font-semibold placeholder:text-slate-400 focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
                                      />
                                      <span className={hasActiveOverride ? "font-semibold text-teal-700" : "text-slate-500"}>%</span>
                                    </span>
                                  </label>
                                );
                              })}
                            </div>

                            <div
                              data-testid={`member-commission-actions-${rowKey}`}
                              className="flex items-center gap-2 pt-0"
                            >
                              <button
                                type="button"
                                data-testid={`member-commission-save-${rowKey}`}
                                data-tutorial-id="settings-team-save-commission"
                                onClick={() => handleSaveCommission(m)}
                                disabled={commissionSaving || !commissionKey}
                                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-50"
                              >
                                {commissionSaving ? "Saving..." : commissionSaved ? "Saved" : "Save commission"}
                              </button>
                              <span className="text-xs text-slate-500">Blank fields fall back to base rate</span>
                            </div>
                          </>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Pending invitations */}
      <section className="bg-white rounded-xl shadow-sm" data-testid="invitations-section">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-900">Pending invitations</h2>
          <span className="text-xs text-slate-500">{invitations.filter(isInvitationActionable).length} pending</span>
        </div>
        {loading ? null : invitations.length === 0 ? (
          <div className="px-6 py-10 text-center text-sm text-slate-400">No invitations yet.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {invitations.map((inv) => {
              const effectiveStatus = invitationEffectiveStatus(inv);
              return (
                <li key={inv.id} data-testid={`invitation-row-${inv.id}`} className="px-6 py-3 flex items-center gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-900 truncate">
                      {inv.invitee_full_name ? `${inv.invitee_full_name} · ${inv.email}` : inv.email}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      <span>{ROLE_LABEL[inv.role] ?? inv.role}</span> ·
                      <span className="ml-1">Sent {formatDate(inv.last_sent_at)}</span> ·
                      <span className="ml-1">Expires {formatDate(inv.expires_at)}</span> ·
                      <code className="ml-2 font-mono text-slate-700">{inv.code}</code>
                    </div>
                  </div>
                  <StatusBadge status={effectiveStatus} />
                  {effectiveStatus === "pending" ? (
                    <div className="flex items-center gap-1">
                      <button data-testid={`invitation-copy-${inv.id}`} onClick={() => handleCopyLink(inv)}
                              className="p-1.5 rounded-md text-slate-500 hover:text-teal-700 hover:bg-teal-50"
                              title="Copy link"><Copy className="w-4 h-4"/></button>
                      <button data-testid={`invitation-resend-${inv.id}`} onClick={() => handleResend(inv)}
                              className="p-1.5 rounded-md text-slate-500 hover:text-teal-700 hover:bg-teal-50"
                              title="Resend email"><RefreshCw className="w-4 h-4"/></button>
                      <button data-testid={`invitation-revoke-${inv.id}`} onClick={() => handleRevoke(inv)}
                              className="p-1.5 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                              title="Revoke"><Trash2 className="w-4 h-4"/></button>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {inviteOpen ? (
        <InviteDialog
          onClose={() => setInviteOpen(false)}
          onSent={() => { load(); }}
          inviterName={user?.user_metadata?.full_name || user?.email}
        />
      ) : null}

      {rosterInvite ? (
        <RosterInviteDialog
          member={rosterInvite}
          inviterName={user?.user_metadata?.full_name || user?.email}
          onClose={() => setRosterInvite(null)}
          onSent={() => { load(); }}
        />
      ) : null}
    </div>
  );
}

function todayIsoDate() {
  const d = new Date();
  const offsetMs = d.getTimezoneOffset() * 60 * 1000;
  return new Date(d.getTime() - offsetMs).toISOString().slice(0, 10);
}

function memberRowKey(member) {
  return member?.user_id || member?.doctor_id || member?.staff_id || "";
}

function uniqueIds(ids = []) {
  return Array.from(new Set((ids || []).filter(Boolean)));
}

function branchLabel(branches = [], branchId) {
  return branches.find((branch) => branch.id === branchId)?.name || null;
}

function dateOnly(value) {
  if (!value) return "";
  return String(value).slice(0, 10);
}

function normalizeCommissionRate(value) {
  if (value === "" || value === null || value === undefined) return 0;
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.min(Math.max(n, 0), 1);
}

function commissionRatesEqual(a, b) {
  return Math.abs(normalizeCommissionRate(a) - normalizeCommissionRate(b)) <= 0.000001;
}

function formatCommissionPercent(rate) {
  const percent = normalizeCommissionRate(rate) * 100;
  return String(Number(percent.toFixed(2)));
}

function parseCommissionPercent(value) {
  const raw = String(value ?? "").trim();
  if (!/^\d+(\.\d{1,2})?$/.test(raw)) return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0 || n > 100) return null;
  return n;
}

function categoryOverrideKey(doctorId, category) {
  if (!doctorId || !category) return "";
  return `${doctorId}::${category}`;
}

function countActiveCategoryOverrides(doctorId, rates, edits) {
  if (!doctorId) return 0;
  return PROCEDURE_CATEGORIES.reduce((count, category) => {
    const key = categoryOverrideKey(doctorId, category);
    const value = edits[key] !== undefined
      ? edits[key]
      : (rates[key] === undefined ? "" : formatCommissionPercent(rates[key]));
    return String(value ?? "").trim() === "" ? count : count + 1;
  }, 0);
}

function PlanCapacity({ capacity }) {
  const planLabel = capacity.plan_label || (capacity.plan === "clinic" ? "Clinic" : "Solo");
  const activeStaff = Number(capacity.active_staff ?? 0);
  const activeDoctors = Number(capacity.active_doctors ?? 0);
  const doctorLimit = capacity.doctor_limit === null || capacity.doctor_limit === undefined
    ? null
    : Number(capacity.doctor_limit);
  const doctorsAvailable = doctorLimit === null
    ? null
    : Number(capacity.doctors_available ?? Math.max(doctorLimit - activeDoctors, 0));
  const staffLimit = capacity.staff_limit === null || capacity.staff_limit === undefined
    ? null
    : Number(capacity.staff_limit);
  const staffAvailable = staffLimit === null
    ? null
    : Number(capacity.staff_available ?? Math.max(staffLimit - activeStaff, 0));
  const doctorLimitPerBranch = capacity.doctor_limit_per_branch === null || capacity.doctor_limit_per_branch === undefined
    ? null
    : Number(capacity.doctor_limit_per_branch);
  const activeBranches = Number(capacity.active_branches ?? 1);
  const branchLimit = capacity.branch_limit === null || capacity.branch_limit === undefined
    ? null
    : Number(capacity.branch_limit);

  return (
    <section
      data-testid="team-plan-capacity"
      className="grid gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:grid-cols-[1.2fr_1fr_1fr]"
    >
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Plan capacity</p>
        <p className="mt-1 text-sm font-semibold text-slate-900">{planLabel} plan</p>
        <p className="mt-1 text-xs text-slate-500">Active seats only. Deactivated people do not count.</p>
        {branchLimit !== null ? (
          <p className="mt-1 text-xs text-slate-500">{activeBranches} of {branchLimit} branches active</p>
        ) : null}
      </div>
      <div className="rounded-lg border border-slate-100 px-3 py-2">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Staff</p>
        <p className="mt-1 text-sm font-semibold text-slate-900">
          {staffLimit === null ? `${activeStaff} active` : `${activeStaff} of ${staffLimit} active`}
        </p>
        <p className="mt-0.5 text-xs text-slate-500">
          {staffLimit === null ? "No hard cap" : `${staffAvailable} available`}
        </p>
      </div>
      <div className="rounded-lg border border-slate-100 px-3 py-2">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Dentists</p>
        <p className="mt-1 text-sm font-semibold text-slate-900">
          {doctorLimit === null ? `${activeDoctors} active` : `${activeDoctors} of ${doctorLimit} active`}
        </p>
        <p className="mt-0.5 text-xs text-slate-500">
          {doctorLimit === null ? "No hard cap" : `${doctorsAvailable} available`}
        </p>
        {doctorLimitPerBranch !== null ? (
          <p className="mt-0.5 text-xs text-slate-500">{doctorLimitPerBranch} dentists per branch</p>
        ) : null}
      </div>
    </section>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return "today";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function StatusBadge({ status }) {
  const map = {
    pending:  { bg: "bg-amber-50",  fg: "text-amber-700",  label: "Pending"  },
    accepted: { bg: "bg-emerald-50",fg: "text-emerald-700",label: "Accepted" },
    revoked:  { bg: "bg-slate-100", fg: "text-slate-600",  label: "Revoked"  },
    expired:  { bg: "bg-slate-100", fg: "text-slate-600",  label: "Expired"  },
  };
  const s = map[status] ?? map.pending;
  return <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full ${s.bg} ${s.fg}`}>{s.label}</span>;
}

function invitationEffectiveStatus(inv, now = new Date()) {
  if (inv?.status === "pending" && invitationExpired(inv?.expires_at, now)) {
    return "expired";
  }
  return inv?.status || "pending";
}

function isInvitationActionable(inv) {
  return invitationEffectiveStatus(inv) === "pending";
}

function invitationExpired(expiresAt, now = new Date()) {
  if (!expiresAt) return false;
  const expires = new Date(expiresAt);
  if (Number.isNaN(expires.getTime())) return false;
  return expires.getTime() < now.getTime();
}

function invitationUrl(code) {
  const inviteCode = String(code || "").trim();
  if (!inviteCode) return "";
  return `${window.location.origin}/join?code=${encodeURIComponent(inviteCode)}`;
}

async function copyInvitationUrl(url) {
  if (!url) {
    toast.error("No invitation link available.");
    return;
  }
  try {
    await copyTextToClipboard(url);
    toast.success("Invitation link copied to clipboard.");
  } catch {
    toast.error("Could not access clipboard. Copy the invite link manually.");
  }
}

function InviteLinkPanel({ code, testPrefix }) {
  const url = invitationUrl(code);
  if (!url) return null;
  const inputId = `${testPrefix}-link`;

  return (
    <div data-testid={`${testPrefix}-link-panel`} className="rounded-lg border border-teal-100 bg-teal-50 p-3">
      <label className="text-xs font-semibold uppercase tracking-wider text-teal-800" htmlFor={inputId}>
        Invite link
      </label>
      <div className="mt-1 flex items-center gap-2">
        <input
          id={inputId}
          data-testid={`${testPrefix}-link-input`}
          type="text"
          readOnly
          value={url}
          onFocus={(e) => e.target.select()}
          className="min-w-0 flex-1 rounded-md border border-teal-200 bg-white px-3 py-2 font-mono text-xs text-slate-700 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
        />
        <button
          type="button"
          data-testid={`${testPrefix}-copy-link`}
          onClick={() => copyInvitationUrl(url)}
          className="inline-flex items-center gap-1.5 rounded-md border border-teal-200 bg-white px-3 py-2 text-xs font-semibold text-teal-700 hover:bg-teal-100"
        >
          <Copy className="h-3.5 w-3.5" />
          Copy
        </button>
      </div>
    </div>
  );
}

function RosterInviteDialog({ member, inviterName, onClose, onSent }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [sentInvite, setSentInvite] = useState(null);
  const name = member?.full_name || member?.doctor_display_name || "Roster member";
  const role = member?.role || "doctor";

  async function handleSubmit(e) {
    e.preventDefault();
    setErr(null);
    setSentInvite(null);
    setBusy(true);
    const { data, error } = await supabase.rpc("invite_user_to_clinic", {
      p_email: email.trim(),
      p_role: role,
      p_invitee_full_name: name,
      p_invitee_display_name: role === "doctor" ? name : null,
      p_links_to_doctor_id: member?.doctor_id || null,
      p_links_to_staff_id: member?.staff_id || null,
    });
    if (error) {
      setBusy(false);
      setErr(error.message);
      return;
    }

    const mail = await sendInvitationEmail({
      to_email: data.email,
      clinic_name: data.clinic_name,
      clinic_slug: data.clinic_slug,
      role: data.role,
      code: data.code,
      invited_by: data.invited_by || inviterName || "The clinic admin",
    });
    setBusy(false);
    if (mail.ok) {
      toast.success(`Invitation sent to ${data.email}.`);
    } else {
      toast.warning(`Invitation created but email failed to send — use Copy Link to send manually. (${mail.code})`);
    }
    setSentInvite(data);
    onSent?.();
  }

  return (
    <div className="fixed inset-0 z-40 bg-slate-900/40 flex items-center justify-center px-4" role="dialog" aria-modal="true" data-testid="roster-invite-dialog">
      <div className="w-full max-w-md bg-white rounded-xl shadow-xl border border-slate-200 p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Send invite</h2>
            <p className="text-xs text-slate-500 mt-1">Add a login email for {name} without creating a duplicate roster row.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none" aria-label="Close">×</button>
        </div>
        {sentInvite ? (
          <div className="space-y-4">
            <InviteLinkPanel code={sentInvite.code} testPrefix="roster-invite" />
            <div className="flex items-center justify-end gap-2 pt-2">
              <button type="button" onClick={onClose} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-md">Done</button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="roster-invite-form">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="roster-invite-email">Email</label>
              <input
                id="roster-invite-email"
                data-testid="roster-invite-email"
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none"
                placeholder="doctor@clinic.example"
              />
            </div>
            {err ? (
              <p data-testid="roster-invite-error" className="text-sm text-rose-600 flex items-start gap-1.5">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0"/><span>{err}</span>
              </p>
            ) : null}
            <div className="flex items-center justify-end gap-2 pt-2">
              <button type="button" onClick={onClose} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-md">Cancel</button>
              <button type="submit" data-testid="roster-invite-submit" disabled={busy}
                      className="px-4 py-2 rounded-md bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 text-white text-sm font-semibold">
                {busy ? "Sending..." : "Send invite"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function InviteDialog({ onClose, onSent, inviterName }) {
  const [name, setName]   = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole]   = useState("doctor");
  const [busy, setBusy]   = useState(false);
  const [err, setErr]     = useState(null);
  const [sentInvite, setSentInvite] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    setErr(null); setSentInvite(null); setBusy(true);
    const { data, error } = await supabase.rpc("invite_user_to_clinic", {
      p_email: email.trim(),
      p_role: role,
      p_invitee_full_name: name.trim() || null,
      p_invitee_display_name: role === "doctor" ? (name.trim() || null) : null,
      p_links_to_doctor_id: null,
      p_links_to_staff_id: null,
    });
    if (error) { setBusy(false); setErr(error.message); return; }

    const mail = await sendInvitationEmail({
      to_email:    data.email,
      clinic_name: data.clinic_name,
      clinic_slug: data.clinic_slug,
      role:        data.role,
      code:        data.code,
      // Prefer the server-resolved inviter name (from public.profiles.full_name)
      // so the email body stays truthful even if auth.user_metadata is empty.
      invited_by:  data.invited_by || inviterName || "The clinic admin",
    });
    setBusy(false);
    if (mail.ok) {
      toast.success(`Invitation sent to ${data.email}.`);
    } else {
      toast.warning(
        `Invitation created but email failed to send — use Copy Link to send manually. (${mail.code})`
      );
    }
    setSentInvite(data);
    onSent?.();
  }

  return (
    <div className="fixed inset-0 z-40 bg-slate-900/40 flex items-center justify-center px-4" role="dialog" aria-modal="true" data-testid="invite-dialog" data-tutorial-id="settings-team-invite-dialog">
      <div className="w-full max-w-md bg-white rounded-xl shadow-xl border border-slate-200 p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Invite a team member</h2>
            <p className="text-xs text-slate-500 mt-1">They'll get an email with a secure join link that expires in 7 days.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none" aria-label="Close">×</button>
        </div>
        {sentInvite ? (
          <div className="space-y-4">
            <InviteLinkPanel code={sentInvite.code} testPrefix="invite" />
            <div className="flex items-center justify-end gap-2 pt-2">
              <button type="button" onClick={onClose} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-md">Done</button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="invite-form">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="invite-name">Name</label>
              <input id="invite-name" data-testid="invite-name-input" data-tutorial-id="settings-team-invite-name" type="text" required autoFocus
                     value={name} onChange={(e) => setName(e.target.value)}
                     className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none"
                     placeholder="Dr. Maria Santos" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="invite-email">Email</label>
              <input id="invite-email" data-testid="invite-email-input" data-tutorial-id="settings-team-invite-email" type="email" required
                     value={email} onChange={(e) => setEmail(e.target.value)}
                     className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none"
                     placeholder="teammate@clinic.example" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="invite-role">Role</label>
              <select id="invite-role" data-testid="invite-role-select" data-tutorial-id="settings-team-invite-role" value={role} onChange={(e) => setRole(e.target.value)}
                      className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white">
                {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            {err ? (
              <p data-testid="invite-error" className="text-sm text-rose-600 flex items-start gap-1.5">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0"/><span>{err}</span>
              </p>
            ) : null}
            <div className="flex items-center justify-end gap-2 pt-2">
              <button type="button" onClick={onClose} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-md">Cancel</button>
              <button type="submit" data-testid="invite-submit-button" data-tutorial-id="settings-team-invite-submit" disabled={busy}
                      className="px-4 py-2 rounded-md bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 text-white text-sm font-semibold">
                {busy ? "Sending…" : "Send Invitation"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
